import { useCallback, useState } from "react";

// CR-013 (SOL-013 §3.7): most-recent-searches list, stored client-side in
// localStorage. This is deliberately separate from `fetchSearchHistory()`
// (ui/web/src/lib/search/svc.ts, GET /api/search/search-history) — that
// endpoint serves a different purpose (server-side audit/cross-device
// history) and is NOT replaced by this hook.
const STORAGE_KEY = "vnp:search-history";
const MAX_HISTORY = 10;

function readHistory(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((q) => typeof q === "string") : [];
  } catch {
    // localStorage unavailable (private mode) or corrupt JSON — start empty,
    // never throw.
    return [];
  }
}

export interface UseSearchHistoryResult {
  /** Most recent queries first, capped at MAX_HISTORY, no duplicates. */
  history: string[];
  /** Record a new query at the front of history (dedup + cap). */
  record: (query: string) => void;
  /** Clear all recorded history. */
  clear: () => void;
}

export function useSearchHistory(): UseSearchHistoryResult {
  const [history, setHistory] = useState<string[]>(() => readHistory());

  const record = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setHistory((prev) => {
      const next = [trimmed, ...prev.filter((q) => q !== trimmed)].slice(0, MAX_HISTORY);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage not available (private mode/quota exceeded) — don't
        // block search on this, just skip persistence for this call.
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setHistory([]);
  }, []);

  return { history, record, clear };
}
